import { FC } from 'react';
import {
  Card,
  Stack,
  Group,
  Text,
  LoadingOverlay,
  ScrollArea,
  Paper,
  Badge,
  Box,
} from '@mantine/core';
import { IconMessage } from '@tabler/icons-react';
import { Annotation, Communication, Practitioner } from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../utils/common-helpers';
import { formatHumanName } from '@medplum/core';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ResourceAvatar } from '@medplum/react';
import { defaultMarkdownComponents } from '../../components/MarkdownComponents';
import { DiagnosticReportReviewAction } from '../../lims-types';

// -----------------------------------------------------------------------------
interface CommunicationWithPractitioner extends Communication {
  practitioner?: Practitioner;
}

// -----------------------------------------------------------------------------
interface CommunicationsCardProps {
  communications: CommunicationWithPractitioner[];
  communicationsLoading: boolean;
}

// -----------------------------------------------------------------------------
export const CommunicationsCard: FC<CommunicationsCardProps> = ({
  communications,
  communicationsLoading,
}) => {
  const { t } = useTranslation('lims');

  const getReviewStatusAndColor = (
    note: Annotation | undefined
  ): { status: DiagnosticReportReviewAction; color: string } => {
    if (!note) {
      return { status: 'comment', color: 'gray' };
    }

    const status = note.text.replace(
      'Review status: ',
      ''
    ) as DiagnosticReportReviewAction;

    if (status === 'approved') {
      return { status: 'approved', color: 'green' };
    }

    if (status === 'changes-requested') {
      return { status: 'changes-requested', color: 'red' };
    }

    return { status: 'comment', color: 'blue' };
  };

  // ---------------------------------------------------------------------------
  return (
    <Card shadow="sm" padding="md" withBorder>
      <Stack gap="md">
        <Group justify="flex-start" align="center" gap="8px">
          <IconMessage size={20} />
          <Text size="lg" fw={600}>
            {t('review.communications.title')}
          </Text>
          {communicationsLoading && <LoadingOverlay visible />}
        </Group>

        {communications.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="xl">
            {t('review.communications.noCommunications')}
          </Text>
        ) : (
          <ScrollArea h={300}>
            <Stack gap="sm">
              {communications.map((comm) => {
                const { status, color } = getReviewStatusAndColor(
                  comm.note?.[0]
                );

                return (
                  <Paper key={comm.id} p="sm" withBorder radius="md">
                    <Stack gap="xs">
                      <Group justify="space-between" align="flex-start">
                        <Group gap="sm">
                          <ResourceAvatar
                            value={comm.practitioner}
                            radius="xl"
                            size="md"
                          />
                          <div>
                            <Text size="sm" fw={600}>
                              {formatHumanName(comm.practitioner?.name?.[0]) ||
                                t('review.communications.unknownUser')}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {comm.sent
                                ? formatDate(comm.sent)
                                : t('review.communications.unknownDate')}
                            </Text>
                          </div>
                        </Group>
                        <Badge variant="light" color={color}>
                          {t(`review.comments.communicationLabels.${status}`, {
                            ns: 'lims',
                          })}
                        </Badge>
                      </Group>
                      <Box ml="36px">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={defaultMarkdownComponents}
                        >
                          {comm.payload?.[0]?.contentString ||
                            t('review.communications.noContent')}
                        </ReactMarkdown>
                      </Box>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </ScrollArea>
        )}
      </Stack>
    </Card>
  );
};
